import React, { useState, useEffect, useCallback } from 'react';
import { Download, Play, RefreshCw, Trash2, CheckCircle, Clock, AlertCircle, FileJson, Archive } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function BulkKernelGenerator() {
  const [startDate, setStartDate] = useState('2026-02-01');
  const [endDate, setEndDate] = useState('2026-12-31');
  const [jobs, setJobs] = useState([]);
  const [currentJob, setCurrentJob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);

  // Fetch all jobs on component mount
  useEffect(() => {
    fetchJobs();
    return () => {
      if (pollingInterval) clearInterval(pollingInterval);
    };
  }, []);

  const fetchJobs = async () => {
    try {
      const response = await fetch(`${API_URL}/api/bulk-kernel/jobs`);
      const data = await response.json();
      setJobs(data.jobs || []);
    } catch (err) {
      console.error('Error fetching jobs:', err);
    }
  };

  const startGeneration = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/bulk-kernel/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          start_date: startDate,
          end_date: endDate
        })
      });

      const data = await response.json();

      if (data.success) {
        setCurrentJob(data.job_id);
        // Start polling for status
        const interval = setInterval(() => pollJobStatus(data.job_id), 3000);
        setPollingInterval(interval);
        fetchJobs();
      } else {
        setError(data.detail || 'Failed to start generation');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const pollJobStatus = useCallback(async (jobId) => {
    try {
      const response = await fetch(`${API_URL}/api/bulk-kernel/status/${jobId}`);
      const data = await response.json();

      if (data.status === 'completed' || data.status === 'failed') {
        if (pollingInterval) {
          clearInterval(pollingInterval);
          setPollingInterval(null);
        }
        fetchJobs();
      }

      // Update jobs list with new status
      setJobs(prev => prev.map(job => 
        job.job_id === jobId ? { ...job, ...data } : job
      ));
    } catch (err) {
      console.error('Error polling job status:', err);
    }
  }, [pollingInterval]);

  const downloadKernels = async (jobId, format = 'zip') => {
    try {
      const response = await fetch(`${API_URL}/api/bulk-kernel/download/${jobId}?format=${format}`);
      
      if (format === 'zip') {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `bulk_kernels_${jobId}.zip`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      } else {
        const data = await response.json();
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `bulk_kernels_${jobId}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      }
    } catch (err) {
      setError(`Download failed: ${err.message}`);
    }
  };

  const deleteJob = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this job and all its kernels?')) {
      return;
    }

    try {
      await fetch(`${API_URL}/api/bulk-kernel/job/${jobId}`, { method: 'DELETE' });
      fetchJobs();
    } catch (err) {
      setError(`Delete failed: ${err.message}`);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'running':
        return <RefreshCw className="w-5 h-5 text-blue-500 animate-spin" />;
      case 'queued':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'failed':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 text-white p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">
            Bulk Kernel Generator
          </h1>
          <p className="text-slate-400 mt-2">
            Generate astrological kernels for every hour of every day in a date range.
            Location: Noida, Uttar Pradesh
          </p>
        </div>

        {/* Generation Form */}
        <div className="bg-slate-800/50 rounded-xl p-6 mb-8 border border-slate-700">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Play className="w-5 h-5 text-amber-400" />
            New Generation
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-2">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-amber-500 focus:outline-none"
                data-testid="start-date-input"
              />
            </div>

            <div>
              <label className="block text-sm text-slate-400 mb-2">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-amber-500 focus:outline-none"
                data-testid="end-date-input"
              />
            </div>

            <div className="flex items-end">
              <button
                onClick={startGeneration}
                disabled={loading}
                className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-semibold py-2 px-6 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="start-generation-btn"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    Starting...
                  </>
                ) : (
                  <>
                    <Play className="w-5 h-5" />
                    Start Generation
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Calculation preview */}
          {startDate && endDate && (
            <div className="mt-4 p-4 bg-slate-900/50 rounded-lg">
              <p className="text-sm text-slate-400">
                <span className="text-amber-400 font-semibold">
                  {Math.max(0, Math.ceil((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24)) + 1)}
                </span> days × <span className="text-amber-400 font-semibold">24</span> hours = 
                <span className="text-amber-400 font-semibold ml-1">
                  {Math.max(0, (Math.ceil((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24)) + 1) * 24).toLocaleString()}
                </span> kernels
              </p>
            </div>
          )}

          {error && (
            <div className="mt-4 p-4 bg-red-900/30 border border-red-700 rounded-lg text-red-400">
              {error}
            </div>
          )}
        </div>

        {/* Jobs List */}
        <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Archive className="w-5 h-5 text-amber-400" />
              Generation Jobs
            </h2>
            <button
              onClick={fetchJobs}
              className="text-slate-400 hover:text-white transition-colors"
              data-testid="refresh-jobs-btn"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>

          {jobs.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              No generation jobs yet. Start one above!
            </div>
          ) : (
            <div className="space-y-4">
              {jobs.map((job) => (
                <div
                  key={job.job_id}
                  className="bg-slate-900/50 rounded-lg p-4 border border-slate-700 hover:border-slate-600 transition-colors"
                  data-testid={`job-card-${job.job_id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {getStatusIcon(job.status)}
                        <span className="font-mono text-sm text-slate-300">{job.job_id}</span>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          job.status === 'completed' ? 'bg-green-900/50 text-green-400' :
                          job.status === 'running' ? 'bg-blue-900/50 text-blue-400' :
                          job.status === 'queued' ? 'bg-yellow-900/50 text-yellow-400' :
                          'bg-red-900/50 text-red-400'
                        }`}>
                          {job.status}
                        </span>
                      </div>

                      <div className="text-sm text-slate-400 space-y-1">
                        <p>
                          <span className="text-slate-500">Date Range:</span>{' '}
                          {job.start_date} → {job.end_date}
                        </p>
                        <p>
                          <span className="text-slate-500">Progress:</span>{' '}
                          {job.processed || 0} / {job.total || 0} kernels
                          ({job.progress?.toFixed(1) || 0}%)
                        </p>
                        {job.current_date && job.status === 'running' && (
                          <p>
                            <span className="text-slate-500">Processing:</span>{' '}
                            {job.current_date}
                          </p>
                        )}
                        <p>
                          <span className="text-slate-500">Created:</span>{' '}
                          {formatDate(job.created_at)}
                        </p>
                      </div>

                      {/* Progress bar */}
                      {job.status === 'running' && (
                        <div className="mt-3 h-2 bg-slate-700 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-amber-500 to-orange-500 transition-all duration-300"
                            style={{ width: `${job.progress || 0}%` }}
                          />
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      {job.status === 'completed' && (
                        <>
                          <button
                            onClick={() => downloadKernels(job.job_id, 'zip')}
                            className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
                            data-testid={`download-zip-${job.job_id}`}
                          >
                            <Archive className="w-4 h-4" />
                            ZIP
                          </button>
                          <button
                            onClick={() => downloadKernels(job.job_id, 'json')}
                            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                            data-testid={`download-json-${job.job_id}`}
                          >
                            <FileJson className="w-4 h-4" />
                            JSON
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => deleteJob(job.job_id)}
                        className="flex items-center gap-2 bg-red-600/30 hover:bg-red-600 text-red-400 hover:text-white px-4 py-2 rounded-lg transition-colors"
                        data-testid={`delete-job-${job.job_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Info Section */}
        <div className="mt-8 bg-slate-800/30 rounded-xl p-6 border border-slate-700/50">
          <h3 className="text-lg font-semibold text-amber-400 mb-3">What's Included in Each Kernel</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-slate-400">
            <div>
              <h4 className="text-white font-medium mb-2">Charts</h4>
              <ul className="list-disc list-inside space-y-1">
                <li>D1 (Rashi) - Main birth chart</li>
                <li>D9 (Navamsa) - Marriage & dharma</li>
                <li>D10 (Dasamsa) - Career chart</li>
                <li>D60 (Shashtiamsa) - Past life karma</li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-medium mb-2">Dasha System</h4>
              <ul className="list-disc list-inside space-y-1">
                <li>MD (Mahadasha) - Major periods</li>
                <li>AD (Antardasha) - Sub-periods</li>
                <li>PD (Pratyantardasha) - Sub-sub-periods</li>
                <li>SD (Sookshma Dasha) - Micro periods</li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-medium mb-2">Special Points</h4>
              <ul className="list-disc list-inside space-y-1">
                <li>Indu Lagna - Wealth indicator</li>
                <li>Lagna position and degree</li>
                <li>All planet positions</li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-medium mb-2">Location</h4>
              <ul className="list-disc list-inside space-y-1">
                <li>Fixed: Noida, Uttar Pradesh</li>
                <li>Latitude: 28.5355°N</li>
                <li>Longitude: 77.3910°E</li>
                <li>Timezone: Asia/Kolkata (IST)</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
